/**
 * H8 (Frank, 18 oktober 2026): terugschrijven per soort.
 *
 * Onder de schakelaar "Wijzigingen terugschrijven" staan drie regels: notities,
 * sessielogs en bijlagen. Een soort zonder eigen regel ERFT de algemene
 * schakelaar, en dat staat er dan ook bij; wie hier niets aanraakt, merkt niets.
 *
 * Een omzetting gaat meteen naar het schakelbord (`PUT /api/grenzen` met een
 * gebied), net als op het tabblad Grenzen. De backend weigert een soort die niet
 * bestaat; die weigering wordt getoond zoals hij is.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

export const SOORTEN = ["notitie", "sessielog", "bijlage"];

/** Het label van een soort. PUUR. */
export function soortNaam(soort, t) {
  if (soort === "notitie") return t("Notities");
  if (soort === "sessielog") return t("Sessielogs");
  if (soort === "bijlage") return t("Bijlagen");
  return soort;
}

/** De soorten van `vault_write` uit het antwoord van het schakelbord. PUUR. */
export function soortenUit(data) {
  const grens = (data?.grenzen || []).find((g) => g.sleutel === "vault_write");
  return (grens?.gebieden || []).filter((g) => SOORTEN.includes(g.gebied));
}

export default function SchrijfSoorten() {
  const { t } = useTaal();
  const [soorten, setSoorten] = useState([]);
  const [bezig, setBezig] = useState("");

  const laden = useCallback(async () => {
    try {
      const antwoord = await axios.get(`${API}/grenzen`);
      setSoorten(soortenUit(antwoord?.data));
    } catch {
      setSoorten([]);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  async function omzetten(s) {
    setBezig(s.gebied);
    try {
      await axios.put(`${API}/grenzen`, {
        sleutel: "vault_write", gebied: s.gebied, open: !s.open, reden: "Instellingen, per soort",
      });
      await laden();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Omzetten is niet gelukt."));
    } finally {
      setBezig("");
    }
  }

  if (!soorten.length) return null;
  return (
    <div className="space-y-2 pl-1" data-testid="schrijf-soorten">
      <p className="text-xs text-muted-foreground">{t("Per soort:")}</p>
      {soorten.map((s) => (
        <div key={s.gebied} className="flex items-center justify-between gap-3">
          <span className="text-sm">
            {soortNaam(s.gebied, t)}
            <span className="text-xs text-muted-foreground" data-testid={`soort-${s.gebied}-herkomst`}>
              {" "}{s.eigen ? t("(eigen regel)") : t("(volgt de schakelaar hierboven)")}
            </span>
          </span>
          <Switch
            checked={s.open}
            disabled={bezig === s.gebied}
            onCheckedChange={() => omzetten(s)}
            aria-label={soortNaam(s.gebied, t)}
            data-testid={`soort-${s.gebied}`}
          />
        </div>
      ))}
      <p className="text-xs text-muted-foreground">
        {t("Een bijlage (geen markdown) wordt ook met deze regel aan nooit teruggeschreven.")}
      </p>
    </div>
  );
}
